import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Sparkles, Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pkceSupabase } from '@/integrations/supabase/pkceClient';

const Auth = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [view, setView] = useState<'login' | 'forgot' | 'reset'>('login');
  
  const { signIn, resetPassword, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isRecoveryFlow =
    searchParams.get('mode') === 'reset' ||
    searchParams.get('type') === 'recovery' ||
    Boolean(searchParams.get('code')) ||
    window.location.hash.includes('type=recovery') ||
    window.location.hash.includes('access_token=');

  useEffect(() => {
    // Don’t redirect away from /auth while the password recovery flow is in progress.
    if (user && !isRecoveryFlow) {
      navigate('/recruiter', { replace: true });
    }
  }, [user, navigate, isRecoveryFlow]);

  // Check if this is a password reset callback
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'reset') {
      setView('reset');
    }
  }, [searchParams]);

  // Password recovery links may arrive with either:
  // - ?code=... (PKCE) or
  // - #access_token=...&refresh_token=...&type=recovery (implicit)
  // We must exchange/consume that into a session before updateUser() can work.
  useEffect(() => {
    if (!isRecoveryFlow) return;

    let cancelled = false;

    const consumeRecoveryParams = async () => {
      const code = searchParams.get('code');

      if (code) {
        const { error } = await pkceSupabase.auth.exchangeCodeForSession(code);
        if (cancelled) return;

        if (error) {
          toast({
            title: 'Reset link expired or invalid',
            description: error.message,
            variant: 'destructive',
          });
          setView('forgot');
          navigate('/auth', { replace: true });
          return;
        }

        setView('reset');
        // Clean up URL (remove code) while staying on reset screen
        navigate('/auth?mode=reset', { replace: true });
        return;
      }

      if (window.location.hash.includes('access_token=')) {
        // Recovery links can arrive with tokens in the hash. We MUST store them as a session
        // otherwise updateUser() will fail with "Auth session missing!".
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const access_token = hashParams.get('access_token');
        const refresh_token = hashParams.get('refresh_token');

        if (!access_token || !refresh_token) {
          toast({
            title: 'Reset link incomplete',
            description: 'Please request a new password reset email.',
            variant: 'destructive',
          });
          setView('forgot');
          navigate('/auth', { replace: true });
          return;
        }

        const { error } = await pkceSupabase.auth.setSession({ access_token, refresh_token });
        if (cancelled) return;

        if (error) {
          toast({
            title: 'Reset link expired or invalid',
            description: error.message,
            variant: 'destructive',
          });
          setView('forgot');
          navigate('/auth', { replace: true });
          return;
        }

        setView('reset');
        // Remove the token hash from the URL
        window.history.replaceState(null, '', '/auth?mode=reset');
        return;
      }

      // Fallback: If we're in recovery mode (/auth?mode=reset) but no tokens are in the URL,
      // it means the server/CDN redirect stripped them or onAuthStateChange already consumed them.
      // Check if a session already exists before showing an error.
      const { data: sessionData } = await pkceSupabase.auth.getSession();
      if (cancelled) return;

      if (sessionData?.session) {
        // Session exists (e.g. set by Supabase's onAuthStateChange), proceed to reset form.
        setView('reset');
        return;
      }

      // No session and no tokens in URL – user navigated here manually without a valid link.
      // Just show the reset form; they'll get "Auth session missing!" if they try to submit.
      setView('reset');
    };

    consumeRecoveryParams();
    return () => {
      cancelled = true;
    };
  }, [isRecoveryFlow, searchParams, navigate, toast]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await signIn(loginEmail.trim(), loginPassword);

    if (error) {
      toast({
        title: 'Login failed',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Welcome back!',
        description: 'Successfully logged in.',
      });
    }

    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await resetPassword(resetEmail.trim());

    if (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Check your email',
        description: 'We sent you a password reset link.',
      });
      setResetEmail('');
    }

    setIsLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure both passwords are the same.',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    // Ensure session is still valid before trying to update.
    const { data: sessionData } = await pkceSupabase.auth.getSession();
    if (!sessionData?.session) {
      toast({
        title: 'Session expired',
        description: 'Please click the reset link in your email again, or request a new one.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    const { error } = await pkceSupabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      toast({
        title: 'Error',
        description:
          error.message === 'Auth session missing!'
            ? 'Your reset link is missing a valid session. Please click the reset link again or request a new one.'
            : error.message,
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Password updated',
        description: 'Your password has been successfully reset.',
      });
      setView('login');
      navigate('/auth', { replace: true });
    }

    setIsLoading(false);
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="login-email">Email</Label>
        <Input
          id="login-email"
          type="email"
          placeholder="recruiter@example.com"
          value={loginEmail}
          onChange={(e) => setLoginEmail(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="login-password">Password</Label>
        <Input
          id="login-password"
          type="password"
          value={loginPassword}
          onChange={(e) => setLoginPassword(e.target.value)}
          required
        />
      </div>
      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Signing in...
          </>
        ) : (
          'Sign In'
        )}
      </Button>
      <div className="text-center">
        <button
          type="button"
          onClick={() => setView('forgot')}
          className="text-sm text-primary hover:underline"
        >
          Forgot your password?
        </button>
      </div>
    </form>
  );

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="reset-email">Email</Label>
        <Input
          id="reset-email"
          type="email"
          placeholder="recruiter@example.com"
          value={resetEmail}
          onChange={(e) => setResetEmail(e.target.value)}
          required
        />
      </div>
      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          'Send Reset Link'
        )}
      </Button>
      <div className="text-center">
        <button
          type="button"
          onClick={() => setView('login')}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to login
        </button>
      </div>
    </form>
  );

  const renderResetPasswordForm = () => (
    <form onSubmit={handleResetPassword} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="new-password">New Password</Label>
        <Input
          id="new-password"
          type="password"
          placeholder="Min. 6 characters"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirm-password">Confirm Password</Label>
        <Input
          id="confirm-password"
          type="password"
          placeholder="Confirm your password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
      </div>
      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Updating...
          </>
        ) : (
          'Update Password'
        )}
      </Button>
    </form>
  );

  const getCardContent = () => {
    switch (view) {
      case 'forgot':
        return {
          title: 'Reset Password',
          description: 'Enter your email to receive a password reset link',
          form: renderForgotPasswordForm(),
        };
      case 'reset':
        return {
          title: 'Set New Password',
          description: 'Enter your new password below',
          form: renderResetPasswordForm(),
        };
      default:
        return {
          title: 'Recruiter Console',
          description: 'Sign in to access your recruitment dashboard',
          form: renderLoginForm(),
        };
    }
  };

  const cardContent = getCardContent();

  return (
    <div className="min-h-screen bg-gradient-subtle flex flex-col items-center justify-center p-4">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <Sparkles className="h-8 w-8 text-primary" />
        <span className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
          RecruitAI
        </span>
      </Link>

      <Card className="w-full max-w-md shadow-elegant">
        <CardHeader>
          <CardTitle className="text-2xl">{cardContent.title}</CardTitle>
          <CardDescription>{cardContent.description}</CardDescription>
        </CardHeader>
        <CardContent>{cardContent.form}</CardContent>
      </Card>

      <p className="mt-6 text-sm text-muted-foreground">
        Looking for jobs?{' '}
        <Link to="/jobs" className="text-primary hover:underline">
          Browse open positions
        </Link>
      </p>
    </div>
  );
};

export default Auth;